import * as React from 'react';

const compareEntries = (a: { entries: () => IterableIterator<[unknown, unknown]> }, b: { entries: () => IterableIterator<[unknown, unknown]>; has: (key: unknown) => boolean; get?: (key: unknown) => unknown; size?: number }): boolean => {
  const mapA = a instanceof Map ? a : new Map(a.entries());
  const mapB = b instanceof Map ? b : new Map(b.entries());
  if (mapA.size !== mapB.size) return false;
  for (const [key, value] of mapA) {
    if (!Object.is(value, mapB.get(key))) return false;
  }
  return true;
};

/** Shallow structural equality — objects, arrays, Maps and Sets one level deep. */
export function shallow<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false;

  if (a instanceof Map && b instanceof Map) return compareEntries(a, b);
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    for (const value of a) if (!b.has(value)) return false;
    return true;
  }

  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) return false;
  for (const key of keysA) {
    if (!Object.hasOwn(b as object, key) || !Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) return false;
  }
  return true;
}

/**
 * Memoizes a selector by shallow equality so a store hook re-renders only when the
 * selected slice actually changes. Mirrors `zustand/react/shallow`'s `useShallow`.
 */
export function useShallow<S, U>(selector: (state: S) => U): (state: S) => U {
  const prev = React.useRef<U>(undefined);
  return state => {
    const next = selector(state);
    return shallow(prev.current as U, next) ? (prev.current as U) : (prev.current = next);
  };
}
